import { Observable, Observer } from 'rxjs';

export interface FileUploadResult<T> {
  content: T;
  file: File;
  progress: number;
}

export interface FileUploadProgress<T> {
  type: 'progress' | 'complete' | 'cancelled';
  fileIndex?: number;
  fileName?: string;
  progress: number;
  content?: T;
  file?: File;
}

export class FileUploadHelper {
  static upload<T>(
    accept: string,
    contentParser: (content: string) => T,
    multiple: boolean = false,
  ): Observable<FileUploadProgress<T>> {
    return new Observable<FileUploadProgress<T>>((observer) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = accept;
      input.multiple = multiple;

      let dialogOpened = false;
      let filesSelected = false;

      const handleWindowFocus = this.createCancellationHandler(observer, () => dialogOpened && !filesSelected);

      input.onchange = (event) => {
        filesSelected = true;
        window.removeEventListener('focus', handleWindowFocus);

        const files = Array.from((event.target as HTMLInputElement).files || []);
        if (!files.length) {
          observer.next({ type: 'cancelled', progress: 0 });
          observer.complete();
          return;
        }

        this.processFiles(files, observer, contentParser);
      };

      input.onclick = (event) => {
        (event.target as HTMLInputElement).value = '';
        dialogOpened = true;
        filesSelected = false;
        window.addEventListener('focus', handleWindowFocus);
      };

      input.click();
      input.remove();
    });
  }

  static download(data: BlobPart, filename: string, type: string = 'text/csv'): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      const blob = new Blob([data], { type });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      observer.next(true);
      observer.complete();
    });
  }

  private static createCancellationHandler<T>(
    observer: Observer<FileUploadProgress<T>>,
    shouldCancel: () => boolean,
  ): () => void {
    const handler = () => {
      setTimeout(() => {
        if (shouldCancel()) {
          observer.next({ type: 'cancelled', progress: 0 });
          observer.complete();
        }
        window.removeEventListener('focus', handler);
      }, 100);
    };
    return handler;
  }

  private static processFiles<T>(
    files: File[],
    observer: Observer<FileUploadProgress<T>>,
    contentParser: (content: string) => T,
  ): void {
    let completedFiles = 0;

    files.forEach((file, index) => {
      const reader = new FileReader();

      reader.onloadstart = () => {
        observer.next({
          type: 'progress',
          fileIndex: index,
          fileName: file.name,
          progress: 0,
        });
      };

      reader.onprogress = (progressEvent) => {
        if (progressEvent.lengthComputable) {
          const progress = (progressEvent.loaded / progressEvent.total) * 100;
          observer.next({
            type: 'progress',
            fileIndex: index,
            fileName: file.name,
            progress,
          });
        }
      };

      reader.onload = (event) => {
        observer.next({
          type: 'complete',
          fileIndex: index,
          fileName: file.name,
          progress: 100,
          content: contentParser(event.target?.result as string),
          file,
        });

        completedFiles++;
        if (completedFiles === files.length) {
          observer.complete();
        }
      };

      reader.onerror = () => {
        observer.error(new Error('Failed to read file'));
      };

      reader.onabort = () => {
        observer.error(new Error('File upload aborted'));
      };

      reader.readAsText(file);
    });
  }

  static convertToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        resolve(dataUrl);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
}
